import { push as pushLocation, replace as replaceLocation } from "react-router-redux";
import { findAuthorizedId } from "./utils";
import ValidationRepository from "./ValidationRepository";
import MiscRepository from "../misc/MiscRepository";
import AcctsRepository from "../accts/AcctsRepository";
import { confirmBackToFront, credFrontToBack, confirmValidationFrontToBack } from "./dataConversion";
import { backToFront } from "../accts/dataConversion";
import { fieldErrorsBackToFront } from "./../misc/dataConversion";
import { UI_ACTIONS, openModal } from "../ui/uiActions";
import { AUTH_ACTIONS } from "../auth/authActions";
import { ACCTS_ACTIONS } from "../accts/acctsActions";
import { MISC_AUTH_ACTIONS } from "../misc/miscActions";
import { RegistrationChannel } from "assets/scripts/domain/validation/validationModels";
import isEmpty from "assets/scripts/utils/isEmpty";
/* --- Action names --- */
export const VALIDATION_ACTIONS = {
    /* Login Page Actions */
    VALIDATION_CONFIRM_NEW_STARTED: "validation-confirm-new-started",
    VALIDATION_CONFIRM_NEW_FAILED: "validation-confirm-new-failed",
    VALIDATION_CONFIRM_NEW_SUCCEEDED_FALSE: "validation-confirm-new-succeeded-false",
    VALIDATION_CONFIRM_NEW_SUCCEEDED_TRUE: "validation-confirm-new-succeeded-true",
    /* Dashboard Page Actions */
    VALIDATION_CONFIRM_ADD_STARTED: "validation-confirm-add-started",
    VALIDATION_CONFIRM_ADD_FAILED: "validation-confirm-add-failed",
    VALIDATION_CONFIRM_ADD_SUCCEEDED_FALSE: "validation-confirm-add-succeeded-false",
    VALIDATION_CONFIRM_ADD_SUCCEEDED_TRUE: "validation-confirm-add-succeeded-true",
    VALIDATION_SET_AUTHORIZED_ID_ADD: "validation-set-auth-id-add",
    /* Validation action names */
    VALIDATION_NEW_STARTED: "validation-new-started",
    VALIDATION_NEW_FAILED: "validation-new-failed",
    VALIDATION_NEW_SUCCEEDED_FALSE: "validation-new-succeeded-false",
    VALIDATION_NEW_SUCCEEDED_TRUE: "validation-new-succeeded-true",
    VALIDATION_ADD_STARTED: "validation-add-started",
    VALIDATION_ADD_FAILED: "validation-add-failed",
    VALIDATION_ADD_SUCCEEDED_FALSE: "validation-add-succeeded-false",
    VALIDATION_ADD_SUCCEEDED_TRUE: "validation-add-succeeded-true",
    VALIDATION_SET_AUTHORIZED_ID_NEW: "validation-set-auth-id-new",
    VALIDATION_ADD_FORM_CHANGE: "validation-add-form-change",
    VALIDATION_TOKEN_QUICKPAY: "validation-token-quickpay",
    REGISTRATION_COMPLETE: "registration-complete",
    SIGNUP_DIRECT_LINK_STARTED: "signup-direct-link-started",
    SIGNUP_DIRECT_LINK_FAILED: "signup-direct-link-failed",
    SIGNUP_DIRECT_LINK_SUCCEEDED: "signup-direct-link-succeeded"
};
/* --- For use on the login page. --- */
export const startQuickPay = () => async (dispatch, getState) => {
    const state = getState();
    const pin = state.validation.validationRequest.pin;
    const ref = state.validation.validationRequest.referenceNumber;
    const channel = state.validation.registrationChannel;
    dispatch({
        type: VALIDATION_ACTIONS.VALIDATION_TOKEN_QUICKPAY,
        payload: {
            tokenLoading: true
        }
    });
    const token = await ValidationRepository.getQuickPayToken(ref, pin, channel ?? RegistrationChannel.Unknown);
    if (token) {
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_TOKEN_QUICKPAY,
            payload: {
                tokenLoading: false
            }
        });
    }
    dispatch(pushLocation(`/public/quickpay/${token}`));
};
/** Sends account credentials to server for account creation, dispatches appropriate actions to store
 *
 * @method confirmNewAccount
 * @param {Object} credentials Value of login form
 * @param {String} captcha Captcha response
 * @returns {Function} Redux thunk
 */
export const confirmNewAccount = (credentials, captcha) => async (dispatch) => {
    dispatch({
        type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_NEW_STARTED,
        payload: credentials
    });
    const mappedCredentials = credFrontToBack(credentials, captcha);
    let confirmationResponse;
    try {
        confirmationResponse = await ValidationRepository.confirmNewAccount(mappedCredentials);
    }
    catch (error) {
        dispatch({ type: MISC_AUTH_ACTIONS.TOGGLE_CAPTCHA, payload: error });
        return dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_NEW_FAILED,
            payload: error
        });
    }
    if (!confirmationResponse.success) {
        const preparedErrors = fieldErrorsBackToFront(confirmationResponse.errors);
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_NEW_SUCCEEDED_FALSE,
            payload: preparedErrors
        });
    }
    else {
        const preparedData = confirmBackToFront(confirmationResponse.data.confirmationRequirements);
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_NEW_SUCCEEDED_TRUE,
            payload: preparedData
        });
        // Send event tracking information
        try {
            await MiscRepository.trackEvent({
                event: "RegisterAccount",
                referenceNumber: credentials.referenceNumber.toString(),
                pin: credentials.pin.toString(),
                channel: RegistrationChannel.LoginPage
            });
        }
        catch (error) {
            // ¯\_(ツ)_/¯
        }
        if (process.env.CLIENT == null && confirmationResponse.data.quickPayOnly) {
            dispatch({
                type: VALIDATION_ACTIONS.VALIDATION_TOKEN_QUICKPAY,
                payload: {
                    tokenLoading: true
                }
            });
            let quickPayToken;
            try {
                quickPayToken = await ValidationRepository.getQuickPayToken(credentials.referenceNumber, credentials.pin, 1);
            }
            catch (error) {
                return dispatch({
                    type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_NEW_FAILED,
                    payload: error
                });
            }
            if (quickPayToken) {
                dispatch({
                    type: VALIDATION_ACTIONS.VALIDATION_TOKEN_QUICKPAY,
                    payload: {
                        tokenLoading: false
                    }
                });
            }
            dispatch(pushLocation(`/public/quickpay/${quickPayToken}`));
        }
        else if (process.env.CLIENT == null && confirmationResponse.data.quickPayEligible) {
            dispatch(pushLocation("/public/options"));
        }
        else {
            dispatch(pushLocation("/public/validation"));
        }
    }
};
const directLinkSignup = (params, channel) => async (dispatch) => {
    dispatch({
        type: VALIDATION_ACTIONS.SIGNUP_DIRECT_LINK_STARTED
    });
    // Validate input parameters
    if ('token' in params && isEmpty(params.token)) {
        dispatch(pushLocation("/public"));
        return dispatch({
            type: VALIDATION_ACTIONS.SIGNUP_DIRECT_LINK_FAILED,
            payload: {
                error: new Error("Invalid token")
            }
        });
    }
    let confirmationResponse;
    try {
        if ('token' in params) {
            // Use token-based authentication
            confirmationResponse = await ValidationRepository.confirmNewAccountToken(params.token, params.captchaResponse);
        }
        else {
            // Use credentials-based authentication
            const mappedCredentials = credFrontToBack(params.credentials, params.captcha);
            confirmationResponse = await ValidationRepository.confirmNewAccount(mappedCredentials);
        }
    }
    catch (error) {
        dispatch({ type: MISC_AUTH_ACTIONS.TOGGLE_CAPTCHA, payload: error });
        return dispatch({
            type: VALIDATION_ACTIONS.SIGNUP_DIRECT_LINK_FAILED,
            payload: {
                error: error
            }
        });
    }
    if (confirmationResponse.success) {
        const preparedData = confirmBackToFront(confirmationResponse.data.confirmationRequirements);
        dispatch({
            type: VALIDATION_ACTIONS.SIGNUP_DIRECT_LINK_SUCCEEDED,
            payload: {
                confirmationRequirements: preparedData,
                referenceNumber: confirmationResponse.data.referenceNumber,
                pin: confirmationResponse.data.pin,
                channel
            }
        });
        // Send event tracking information
        try {
            await MiscRepository.trackEvent({
                event: "RegisterAccount",
                referenceNumber: confirmationResponse.data.referenceNumber.toString(),
                pin: confirmationResponse.data.pin.toString(),
                channel
            });
        }
        catch (error) {
            // ¯\_(ツ)_/¯
        }
        if (process.env.CLIENT == null && confirmationResponse.data.quickPayOnly) {
            dispatch({
                type: VALIDATION_ACTIONS.VALIDATION_TOKEN_QUICKPAY,
                payload: {
                    tokenLoading: true
                }
            });
            const quickPayToken = await ValidationRepository.getQuickPayToken(confirmationResponse.data.referenceNumber, confirmationResponse.data.pin, channel);
            if (quickPayToken) {
                dispatch({
                    type: VALIDATION_ACTIONS.VALIDATION_TOKEN_QUICKPAY,
                    payload: {
                        tokenLoading: false
                    }
                });
            }
            dispatch(replaceLocation(`/public/quickpay/${quickPayToken}`));
        }
        else if (process.env.CLIENT == null && confirmationResponse.data.quickPayEligible) {
            dispatch(replaceLocation("/public/options"));
        }
        else {
            dispatch(replaceLocation("/public/validation"));
        }
    }
};
export const signUpWithToken = (token, captchaResponse) => directLinkSignup({ token, captchaResponse }, RegistrationChannel.RegisterToken);
export const signUpWithQrCode = (referenceNumber, pin, captcha) => directLinkSignup({ credentials: { referenceNumber, pin }, captcha }, RegistrationChannel.QRCode);
/* --- For use on the AccountValidation page. --- */
/**
 * Redirects to the index if no account information is present.
 *
 * @method redirectIfNoAccountInfo
 * @param {Number} refNum Reference number for account to validate
 * @param {Number} pin Pin number for account to validate
 * @returns {Function} Redux thunk
 */
export const redirectIfNoAccountInfo = (refNum, pin) => (dispatch) => {
    if (!refNum || !pin) {
        dispatch(pushLocation("/public"));
    }
};
/**
 * Kicks off validation process by saving potential request to the store
 *
 * @method startValidateNewAccount
 * @param {Object} validationInfo Validation information
 * @returns {Function} Redux thunk
 */
export const startValidateNewAccount = (validationInfo) => {
    return {
        type: VALIDATION_ACTIONS.VALIDATION_NEW_STARTED,
        payload: validationInfo
    };
};
/**
 * Saves the authorizedId to the validation request portion of the store
 *
 * @method setAuthorizedIdNew
 * @param {Number} authId Authorized individual's Id
 * @returns {Function} Redux thunk
 */
export const setAuthorizedIdNew = (authId) => {
    return {
        type: VALIDATION_ACTIONS.VALIDATION_SET_AUTHORIZED_ID_NEW,
        payload: authId
    };
};
export function sendValidateNewAccount(validationInfo, authId) {
    return async (dispatch) => {
        let validationResponse;
        let mappedValidationInfo;
        if (authId != null) {
            mappedValidationInfo = confirmValidationFrontToBack(validationInfo, authId);
        }
        else {
            mappedValidationInfo = validationInfo;
        }
        try {
            validationResponse = await ValidationRepository.validateNewAccount(mappedValidationInfo);
        }
        catch (error) {
            return dispatch({ type: VALIDATION_ACTIONS.VALIDATION_NEW_FAILED, payload: error });
        }
        if ("errors" in validationResponse) {
            const preparedErrors = fieldErrorsBackToFront(validationResponse.errors);
            dispatch({
                type: VALIDATION_ACTIONS.VALIDATION_NEW_SUCCEEDED_FALSE,
                payload: preparedErrors
            });
        }
        else {
            dispatch({
                type: VALIDATION_ACTIONS.VALIDATION_NEW_SUCCEEDED_TRUE,
                payload: validationResponse
            });
            // Send event tracking information
            try {
                await MiscRepository.trackEvent({
                    event: "SelectAuthorizedIndividual",
                    referenceNumber: validationInfo.referenceNumber.toString(),
                    pin: validationInfo.pin.toString()
                });
            }
            catch (error) {
                return error;
            }
            dispatch(pushLocation("/public/createPassword"));
        }
    };
}
/* --- For use on the Dashboard page. --- */
/**
 * Sends account credentials to server for account creation, dispatches appropriate actions to store
 *
 * @method confirmAddAccount
 * @param {Object} credentials Value of add account form
 * @returns {Function} Redux thunk
 */
export const confirmAddAccount = (credentials) => async (dispatch, getState) => {
    dispatch({
        type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_ADD_STARTED,
        payload: credentials
    });
    let confirmResponse;
    try {
        confirmResponse = await ValidationRepository.confirmAddAccount(credentials);
    }
    catch (error) {
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_ADD_FAILED,
            payload: error
        });
        if (error.response?.status === 401) {
            dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: false });
            dispatch(pushLocation("/public"));
        }
        return;
    }
    if (!confirmResponse.success) {
        const preparedErrors = fieldErrorsBackToFront(confirmResponse.errors);
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_ADD_SUCCEEDED_FALSE,
            payload: preparedErrors
        });
        dispatch({
            type: UI_ACTIONS.MODAL_OPEN,
            payload: {
                modalId: "validateAddAccount",
                triggerId: ""
            }
        });
        return;
    }
    const preparedData = confirmBackToFront(confirmResponse.data);
    dispatch({
        type: VALIDATION_ACTIONS.VALIDATION_CONFIRM_ADD_SUCCEEDED_TRUE,
        payload: preparedData
    });
    const state = getState();
    // Send event tracking information
    try {
        await MiscRepository.trackEvent({
            event: "AddPayable",
            referenceNumber: credentials.referenceNumber.toString(),
            pin: credentials.pin.toString()
        });
    }
    catch (error) {
        return error;
    }
    if (preparedData.deceased || preparedData.confirmation.length > 0) {
        dispatch({
            type: UI_ACTIONS.MODAL_OPEN,
            payload: {
                modalId: "validateAddAccount",
                triggerId: ""
            }
        });
        return;
    }
    const { firstName, lastName } = state.profile.profile || {
        firstName: "",
        lastName: ""
    };
    const value = {
        authorized: true,
        pin: parseInt(credentials.pin, 10),
        referenceNumber: parseInt(credentials.referenceNumber, 10),
        relationship: "Self",
        authorizedId: findAuthorizedId(firstName, lastName, preparedData.authorized),
        email: "",
        firstName,
        lastName,
        validation: {}
    };
    dispatch(startValidateAddAccount(value));
    if (value.authorizedId != null) {
        dispatch(sendValidateAddAccount(value, value.authorizedId));
    }
    else {
        dispatch(openModal({ modalId: "authIndividualAdd", triggerId: "" }));
    }
};
/**
 * Kicks off validation process by saving potential request to the store
 *
 * @method startValidateAddAccount
 * @param {Object} validationInfo Validation information
 * @returns {Function} Redux thunk
 */
export const startValidateAddAccount = (validationInfo) => {
    return {
        type: VALIDATION_ACTIONS.VALIDATION_ADD_STARTED,
        payload: validationInfo
    };
};
/**
 * Completes the validation process by sending the store's request data to the API,
 * dispatching appropriate status actions
 *
 * @method sendValidateAddAccount
 * @param {Object} validationInfo Validation information
 * @param {Number} authId Authorized individual's Id
 * @returns {Function} Redux thunk
 */
export const sendValidateAddAccount = (validationInfo, authId) => async (dispatch) => {
    let validationResponse;
    let mappedValidationInfo = null;
    if (authId) {
        mappedValidationInfo = confirmValidationFrontToBack(validationInfo, authId);
    }
    else {
        mappedValidationInfo = validationInfo;
    }
    try {
        validationResponse = await ValidationRepository.validateAddAccount(mappedValidationInfo);
    }
    catch (error) {
        dispatch({ type: VALIDATION_ACTIONS.VALIDATION_ADD_FAILED, payload: error });
        if (error.response?.status === 401) {
            dispatch({ type: AUTH_ACTIONS.LOGOUT, payload: false });
            dispatch(pushLocation("/public"));
        }
        return;
    }
    if (!validationResponse.success) {
        const preparedErrors = fieldErrorsBackToFront(validationResponse.errors);
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_ADD_SUCCEEDED_FALSE,
            payload: preparedErrors
        });
        dispatch({
            type: UI_ACTIONS.MODAL_OPEN,
            payload: {
                modalId: "validateAddAccount",
                triggerId: ""
            }
        });
    }
    else {
        dispatch({
            type: VALIDATION_ACTIONS.VALIDATION_ADD_SUCCEEDED_TRUE,
            payload: validationResponse
        });
        // Update accounts
        dispatch({ type: ACCTS_ACTIONS.ACCTS_LOAD_STARTED });
        let acctsResponse;
        try {
            acctsResponse = await AcctsRepository.getAll();
        }
        catch (error) {
            return dispatch({ type: ACCTS_ACTIONS.ACCTS_LOAD_FAILED, payload: error });
        }
        if ("errors" in acctsResponse) {
            const preparedErrors = fieldErrorsBackToFront(acctsResponse.errors);
            dispatch({
                type: ACCTS_ACTIONS.ACCTS_LOAD_SUCCEEDED_FALSE,
                payload: preparedErrors
            });
        }
        else {
            const preparedData = backToFront(acctsResponse.data);
            dispatch({
                type: ACCTS_ACTIONS.ACCTS_LOAD_SUCCEEDED_TRUE,
                payload: {
                    accts: preparedData,
                    autoAddedPayable: acctsResponse.data.autoAddedPayable
                }
            });
        }
        // Send event tracking information
        try {
            await MiscRepository.trackEvent({
                event: "AddPayable",
                referenceNumber: validationInfo.referenceNumber.toString(),
                pin: validationInfo.pin.toString()
            });
        }
        catch (error) {
            return error;
        }
    }
};
/**
 * Saves the authorizedId to the validation request portion of the store
 *
 * @method setAuthorizedIdAdd
 * @param {Number} authId Authorized individual's Id
 * @returns {Function} Redux thunk
 */
export const setAuthorizedIdAdd = (authId) => {
    return {
        type: VALIDATION_ACTIONS.VALIDATION_SET_AUTHORIZED_ID_ADD,
        payload: authId
    };
};
/* --- For use on the ReferAuthorizedUser page. --- */
/**
 * Tracks landing on the refer authorized person form
 *
 * @method startReferAuthorizedPerson
 * @param {String} referenceNumber Reference number for related account
 * @param {String} pin Pin number for related account
 * @returns {Function} Redux thunk
 */
export const startReferAuthorizedPerson = (referenceNumber, pin) => async () => {
    // Send event tracking information
    try {
        await MiscRepository.trackEvent({
            event: "AddRightPartyContactStart",
            referenceNumber: referenceNumber.toString(),
            pin: pin.toString()
        });
    }
    catch (error) {
        return error;
    }
};
export const addFormChange = (values) => ({
    type: VALIDATION_ACTIONS.VALIDATION_ADD_FORM_CHANGE,
    payload: values
});
export const registrationComplete = (account, profile) => async (dispatch) => {
    dispatch({
        type: VALIDATION_ACTIONS.REGISTRATION_COMPLETE,
        payload: {
            account,
            profile
        }
    });
    dispatch(pushLocation("/secured"));
};



// WEBPACK FOOTER //
// ./src/assets/scripts/domain/validation/validationActions.ts